import { getPropValidator, hasValue, PushErrorNotification, PushSuccessNotification, required } from "@repo/helpers";
import { KeyPairArray } from "@repo/models";
import { TextBox, ToggleSlider, ValidatedForm } from "@repo/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import AuditApi from "../../api/auditApi";
import { usePendingChangesContext } from "../../contexts/pendingChangesContext";
import { AuditConfig, AuditConfigDefinition, ParamDefinition } from "../../models";
import ParamEditor from "../paramEditor";

interface Props {
	definition: AuditConfigDefinition;
	auditConfig: AuditConfig | undefined;
	setAuditConfig: (config: AuditConfig | undefined) => void;
	savedConfig: AuditConfig | undefined;
	setSavedConfig: (config: AuditConfig | undefined) => void;
}

function SingleAuditEditor(props: Props) {
	const { definition, auditConfig, savedConfig, setAuditConfig } = props;

	const settingsQuery = useQuery({
		queryKey: ["Settings"],
		queryFn: AuditApi.settings,
	});

	const sourceParams = useMemo(
		() => props.definition?.ParamDefinition?.filter((s) => s.Source) ?? [],
		[props.definition],
	);
	const config = useRef({ ...auditConfig });
	const [customName, setCustomName] = useState(hasValue(auditConfig?.name));
	//Updating layout doesn't trigger a state change, so we force it
	const [trigger, setTrigger] = useState(false);
	const { pendingChanges, setItemChanged } = usePendingChangesContext();
	const queryClient = useQueryClient();

	useLayoutEffect(() => {
		config.current = {
			...auditConfig,
			paramValues: new KeyPairArray(auditConfig?.paramValues?.data ?? []),
		};
		setTrigger(!trigger);
	}, [auditConfig]);

	useEffect(() => {
		setCustomName(hasValue(auditConfig?.name));
	}, [auditConfig?.id]);

	const valueChanged = (value: any, id?: string) => {
		config.current[id] = value;
		onChange(config.current);
	};

	const paramValueChanged = useCallback(
		function (value: any, id?: string, loaded?: boolean) {
			const paramSource = sourceParams.filter((d) => d.Name === id).map((d) => d.Source)[0];
			//If loads from source, and source not loaded, don't update value
			if (paramSource && !loaded) return;

			if (!config.current?.paramValues) return;

			const paramValues = new KeyPairArray(config.current.paramValues.data);
			paramValues.set({ id, value });
			onChange({ ...config.current, paramValues });
		},
		[sourceParams],
	);

	function onChange(config: AuditConfig) {
		setAuditConfig(config);
		setItemChanged("audit", true);
	}

	function customNameClicked() {
		const newHasCustom = !customName;
		setCustomName(newHasCustom);
		valueChanged(newHasCustom ? props.definition.Name : null, "name");
	}

	const save = async () => {
		try {
			const configuration = {
				EmailRecipients: config.current.email,
				Enabled: config.current.active,
				Code: definition.Code,
			};
			config.current.paramValues.data.filter((v) => v.value).forEach((v) => (configuration[v.id] = v.value));
			const configurations = {};
			configurations[config.current.id || 0] = JSON.stringify(configuration);
			const ret = await AuditApi.saveAuditConfig(config.current, definition);
			PushSuccessNotification("Saved");
			setItemChanged("audit", false);
			return ret;
		} catch (e) {
			PushErrorNotification(e as string);
		} finally {
			await queryClient.invalidateQueries({ queryKey: ["Configs"] });
		}
	};

	const cancel = () => {
		setAuditConfig(savedConfig);
		setItemChanged("audit", false);
	};

	//need list of existing on left
	const params = props.definition?.ParamDefinition || [];
	const validations = {
		email: [required("Email Recipients")],
	};

	return (
		<ValidatedForm
			pendingChanges={pendingChanges}
			id="auditorConfig"
			obj={config.current}
			shouldValidate
			showButtons
			validators={validations}
			save={save}
			cancel={cancel}
		>
			<section id="basicInfo">
				{renderNameEditor()}
				<TextBox
					validator={getPropValidator(...validations.email)}
					id="email"
					label="Email Recipients"
					value={config.current.email}
					changeHandler={valueChanged}
				/>
				<span style={{ fontSize: 12, fontStyle: "italic" }}>(comma separated)</span>
				{settingsQuery.data?.sftpEnabled ? (
					<ParamEditor
						paramDefinition={{
							Name: "SFTPRelativeUrl",
							Type: "Text",
							HelpText: "SFTP Relative URL",
							label: "SFTP Relative URL",
						}}
						definitionId={props.definition?.Id}
						value={config.current?.paramValues?.getValue("SFTPRelativeUrl") ?? ""}
						updateValue={paramValueChanged}
					/>
				) : null}
				<ToggleSlider id="active" label="Active?" value={config.current.active} changeHandler={valueChanged} />
			</section>
			{params.map(renderEditor)}
		</ValidatedForm>
	);

	function renderNameEditor() {
		return (
			<div>
				<ToggleSlider
					id="customName"
					label="Custom Name?"
					value={customName}
					changeHandler={customNameClicked}
				/>
				{customName && (
					<TextBox
						validator={required("Custom Name")}
						id="name"
						label=""
						value={config.current.name}
						changeHandler={valueChanged}
					/>
				)}
			</div>
		);
	}

	function renderEditor(paramDef: ParamDefinition) {
		const paramValues = config.current.paramValues || new KeyPairArray([]);
		const value = paramValues.getValue(paramDef.Name);

		return (
			<ParamEditor
				paramDefinition={paramDef}
				definitionId={props.definition.Id}
				value={value}
				updateValue={paramValueChanged}
			/>
		);
	}
}

export default SingleAuditEditor;
